import api from '@/api';

import { PageRequest } from '@/core/request/PageRequest';
import { PageResult } from '@/core/request/PageResult';

import {
  Company,
  CompanyCreateRequest,
  CompanyPecaAiResponse,
  CompanyReduced,
  CompanyProvider,
  CompanySettingsFormValues,
  CompanySettingsInterface,
  CompanyUpdateRequest,
  ListCompaniesQueryReduced,
  PageResultCompanyReduced,
  CompanyLogoUpdateRequest,
  CompanyLogoUpdateResponse,
  CompanySettingsResponse,
} from './Companies.types';

import { API_BASE_URL } from '..';
import { Omit } from 'lodash';

const CONTROLLER_URL = `${API_BASE_URL}/companies`;

export async function listCompaniesLinkedEmployee(
  params?: PageRequest,
): Promise<PageResult<Company>> {
  const result = (await api.get(`${CONTROLLER_URL}/matrix`, params)) as unknown;

  return result as PageResult<Company>;
}

export async function listCompaniesReduced(
  params?: ListCompaniesQueryReduced,
): Promise<PageResultCompanyReduced> {
  const result = (await api.get(
    `${CONTROLLER_URL}/filter/reduced`,
    params,
  )) as unknown;

  return result as PageResultCompanyReduced;
}

export async function listCompaniesByEmployee(
  employeeId: number,
  params?: PageRequest,
): Promise<PageResult<Company>> {
  return api.get(
    `${CONTROLLER_URL}/employee/${employeeId}`,
    params,
  ) as unknown as PageResult<Company>;
}

export async function createCompany(obj: CompanyCreateRequest) {
  const result = (await api.post(`${CONTROLLER_URL}`, obj, null)) as unknown;

  return result as Company;
}

export async function getCompany(id?: number) {
  const result = (await api.get(`${CONTROLLER_URL}/${id}`, null)) as unknown;

  return result as Company;
}

export async function getCompanyReduced(id: number) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${id}/reduced`,
    null,
  )) as unknown;

  return result as CompanyReduced;
}

export async function deleteCompany(id: number) {
  const result = await api.remove(`${CONTROLLER_URL}/${id}`, {}, null);

  return result;
}

export async function companyActivateOrInactivate(companyId: number) {
  const result = (await api.put(
    `${CONTROLLER_URL}/${companyId}/activate`,
    {},
    null,
  )) as unknown;

  return result;
}

export async function updateCompany(obj: CompanyUpdateRequest) {
  const result = (await api.put(CONTROLLER_URL, obj, null)) as unknown;

  return result;
}

export async function updateLogoCompany(obj: CompanyLogoUpdateRequest) {
  const formData = new FormData();

  formData.append('file', obj.file);

  const result = (await api.patch(
    `${CONTROLLER_URL}/${obj.companyId}/logo`,
    formData,
    null,
  )) as unknown;

  return result as CompanyLogoUpdateResponse;
}

export async function deleteLogoCompany(companyId: number) {
  const result = await api.remove(
    `${CONTROLLER_URL}/${companyId}/logo`,
    {},
    null,
  );
  return result;
}

export async function redirectCompanyPecaAi(companyId: number) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${companyId}/peca-ai-redirect-url`,
    null,
  )) as unknown;

  return result as CompanyPecaAiResponse;
}

export async function getCompanyProvider(companyId?: number) {
  const result = (await api.get(
    `${CONTROLLER_URL}/${companyId}/provider`,
    null,
  )) as unknown;

  return result as CompanyProvider;
}

export async function getCompanySettings(companyId?: number) {
  const result = (await api.get(
    `${API_BASE_URL}/companySettings/${companyId}`,
    null,
  )) as unknown;
  return result as CompanySettingsResponse;
}

export async function createCompanySettings(
  params: Omit<CompanySettingsFormValues, 'id'>,
) {
  const result = (await api.post(
    `${API_BASE_URL}/companySettings`,
    params,
    null,
  )) as unknown;

  return result as CompanySettingsInterface;
}

export async function updateCompanySettings(
  companyId: number,
  params: CompanySettingsFormValues,
) {
  const result = (await api.put(
    `${API_BASE_URL}/companySettings/${companyId}`,
    { companyId, ...params },
    null,
  )) as unknown;

  return result as CompanySettingsInterface;
}

export async function deleteCompanyContact(contactId: number) {
  const result = await api.remove(
    `${CONTROLLER_URL}/contact/${contactId}`,
    {},
    null,
  );
  return result;
}
